/**
 * Shared offer-edit form engine. Owns the serverData-lockstep save pattern
 * (keep `defaultValues` in sync with `reset()` so TanStack Form's per-render
 * `update()` never snaps the form back to a stale prop after save), submit-gate
 * validation via `OfferSchema.safeParse`, and the inline error lookup. Consumed
 * by both the full `OfferForm` and the focused `MiniSprintForm`.
 * Aliases: useEntityForm, offer form engine, save lockstep.
 */
import { useStore } from "@tanstack/react-form";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { useAppForm } from "~/components/form/app-form";
import { OfferSchema, type OfferFormData } from "~/lib/schemas/offer";

import { offerFormOptions } from "./offer-form-options";

export function useEntityForm({
  initialData,
  save,
  successMessage,
}: {
  initialData: OfferFormData;
  save: (values: OfferFormData) => Promise<OfferFormData>;
  successMessage: string;
}) {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Canonical server snapshot the form edits; kept in lockstep with reset().
  const [serverData, setServerData] = useState(initialData);

  const form = useAppForm({
    ...offerFormOptions,
    defaultValues: serverData,
    onSubmit: async ({ value, formApi }) => {
      const fresh = await save(value);
      setServerData(fresh);
      formApi.reset(fresh);
      setSubmitAttempted(false);
      toast.success(successMessage);
    },
  });

  // Adopt freshly-loaded server data when navigating between entities (the
  // entity id changes). Guard by id so a parent re-render that happens to
  // re-create the `initialData` object reference doesn't silently clobber the
  // user's in-progress edits.
  const lastIdRef = useRef(initialData.id);
  useEffect(() => {
    if (initialData.id === lastIdRef.current) return;
    lastIdRef.current = initialData.id;
    setServerData(initialData);
    form.reset(initialData);
  }, [form, initialData]);

  const values = useStore(form.store, (s) => s.values);
  const isDirty = useStore(form.store, (s) => s.isDirty);
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);

  const parsed = useMemo(() => OfferSchema.safeParse(values), [values]);
  const errorsByName = useMemo(() => {
    const m = new Map<string, string>();
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const name = issue.path.join(".");
        if (!m.has(name)) m.set(name, issue.message);
      }
    }
    return m;
  }, [parsed]);
  const errorFor = useCallback(
    (name: string) => (submitAttempted ? errorsByName.get(name) : undefined),
    [submitAttempted, errorsByName],
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setSubmitAttempted(true);
      if (!parsed.success) {
        toast.error(
          `Please fix ${parsed.error.issues.length} field(s) before saving`,
        );
        return;
      }
      void form.handleSubmit();
    },
    [form, parsed],
  );

  return {
    form,
    values,
    isDirty,
    isSubmitting,
    submitAttempted,
    parsed,
    errorFor,
    handleSubmit,
  };
}
